import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Camera, MapPin, CheckCircle2, TrendingUp, Wallet, Banknote, Clock, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { STATUS_META, money } from "@/lib/constants";
import MonthCalendar from "@/components/MonthCalendar";

function StatCard({ label, value, className = "" }) {
  return (
    <div className={`rounded-xl p-3 border ${className}`}>
      <div className="text-2xl font-head font-extrabold">{value}</div>
      <div className="text-[11px] font-medium opacity-80">{label}</div>
    </div>
  );
}

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const [leaveDays, setLeaveDays] = useState(7);
  const { data: dash } = useQuery({ queryKey: ["emp-dash"], queryFn: async () => (await api.get("/dashboard/employee")).data });
  const { data: today } = useQuery({ queryKey: ["emp-today"], queryFn: async () => (await api.get("/attendance/today")).data });
  const { data: upcoming } = useQuery({
    queryKey: ["upcoming-leaves", leaveDays],
    queryFn: async () => (await api.get(`/leaves/upcoming?days=${leaveDays}`)).data,
    refetchInterval: 20000,
  });
  const now = new Date();
  const { data: cal } = useQuery({
    queryKey: ["emp-cal", now.getFullYear(), now.getMonth() + 1],
    queryFn: async () => (await api.get(`/attendance/calendar?year=${now.getFullYear()}&month=${now.getMonth() + 1}`)).data,
  });

  const counts = dash?.counts || {};
  const record = today?.record;
  const window = today?.marking_window;

  return (
    <div className="space-y-5 wp-fade-up">
      <div>
        <p className="text-slate-500 text-sm">Welcome back,</p>
        <h1 className="text-2xl font-head font-extrabold text-slate-900">{dash?.employee?.name || "Employee"}</h1>
        <p className="text-xs text-slate-400">{dash?.employee?.designation} · {dash?.employee?.employee_id}</p>
      </div>

      {/* Today status / Mark button */}
      {record ? (
        <div data-testid="today-status-card" className={`rounded-2xl p-5 border ${STATUS_META[record.status]?.soft}`}>
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8" />
            <div>
              <div className="font-head font-bold text-lg">Marked {STATUS_META[record.status]?.label}</div>
              <div className="text-sm opacity-80">Today at {record.server_time} · {record.activity_type?.replace("_", " ")}</div>
            </div>
          </div>
        </div>
      ) : window === "blocked" ? (
        <div className="rounded-2xl p-5 bg-red-50 border border-red-200 text-red-700">
          <div className="flex items-center gap-2 font-semibold"><Clock className="h-5 w-5" /> Marking Window Closed</div>
          <p className="text-sm mt-1">Attendance marking time has expired. Please contact admin.</p>
        </div>
      ) : (
        <button
          data-testid="mark-attendance-button"
          onClick={() => navigate("/app/mark")}
          className="w-full py-6 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-head font-bold text-lg shadow-lg shadow-blue-500/30 transition-transform active:scale-95 flex items-center justify-center gap-3 wp-pulse"
        >
          <Camera className="h-6 w-6" /> MARK TODAY'S ATTENDANCE
        </button>
      )}

      {/* Salary + advance */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl p-4 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <Wallet className="h-5 w-5 text-blue-300 mb-2" />
          <div className="text-xs text-slate-300">Monthly Salary</div>
          <div className="text-lg font-head font-extrabold">{money(dash?.employee?.monthly_salary)}</div>
        </div>
        <div className="rounded-2xl p-4 bg-white border border-slate-200">
          <Banknote className="h-5 w-5 text-orange-500 mb-2" />
          <div className="text-xs text-slate-500">Advance Outstanding</div>
          <div className="text-lg font-head font-extrabold text-slate-900">{money(dash?.advance_outstanding)}</div>
        </div>
      </div>

      {/* This month counts */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head font-bold text-slate-900">This Month</h3>
          <span className="flex items-center gap-1 text-sm font-semibold text-emerald-600"><TrendingUp className="h-4 w-4" />{dash?.attendance_pct ?? 0}%</span>
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          <StatCard label="Present" value={counts.present ?? 0} className={STATUS_META.present.soft} />
          <StatCard label="Half Day" value={counts.half_day ?? 0} className={STATUS_META.half_day.soft} />
          <StatCard label="Absent" value={counts.absent ?? 0} className={STATUS_META.absent.soft} />
          <StatCard label="Paid Leave" value={counts.paid_leave ?? 0} className={STATUS_META.paid_leave.soft} />
          <StatCard label="Weekly Off" value={counts.weekly_leave ?? 0} className={STATUS_META.weekly_leave.soft} />
          <StatCard label="Holiday" value={counts.govt_holiday ?? 0} className={STATUS_META.govt_holiday.soft} />
        </div>
      </div>

      {/* Upcoming Leave - Your Branch */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200" data-testid="upcoming-leave-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head font-bold text-slate-900 flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-purple-500" /> Upcoming Leave · Your Branch
          </h3>
          <button
            data-testid="upcoming-leave-toggle"
            onClick={() => setLeaveDays((d) => (d === 7 ? 30 : 7))}
            className="text-xs font-semibold text-blue-600 hover:underline"
          >
            {leaveDays === 7 ? "View All (30 days)" : "Show Next 7 days"}
          </button>
        </div>
        <p className="text-[11px] text-slate-400 mb-2">Next {leaveDays} days · approved leaves only</p>
        <div className="space-y-2">
          {(upcoming || []).length === 0 && (
            <p data-testid="upcoming-leave-empty" className="text-sm text-slate-400">No approved upcoming leaves in your branch.</p>
          )}
          {(upcoming || []).map((l) => (
            <div key={l.id} data-testid={`upcoming-leave-${l.id}`} className="flex items-center justify-between rounded-xl border border-purple-100 bg-purple-50/50 px-3 py-2.5">
              <div className="min-w-0">
                <div className="font-medium text-slate-900 truncate">{l.employee_name}</div>
                <div className="text-xs text-slate-500">{l.from_date} → {l.to_date}</div>
              </div>
              <span className="shrink-0 text-xs font-semibold text-purple-700 bg-white border border-purple-200 rounded-full px-2.5 py-1">{l.days} day{l.days > 1 ? "s" : ""}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200">
        <MonthCalendar year={now.getFullYear()} month={now.getMonth() + 1} days={cal?.days} />
      </div>
    </div>
  );
}
